import { useState, useEffect, useRef } from "react";
import "./App.css";
import { NewRoom } from "./NewRoom";
import { Tutorial } from "./Pages/Tutorial/Tutorial";
import { Background } from "./Components/Background";
import Menu from "./Pages/Menu";
import StartPage from "./Pages/StartPage";
import CreateRoom from "./Pages/CreateRoom";
import JoinRoom from "./Pages/JoinRoom";

import socketIO from "socket.io-client";

import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "./i18n";

import { InfoMessage } from "./Components/InfoMessage/InfoMessage";

import useThemeColor from "./hooks/TopBarColorWheel";
import useContainerHeight from "./hooks/useContainerHeight";
import useScrollTrap from "./hooks/useScrollTrap";
import useIsMobile from "./hooks/useIsMobile";
import AIRoom from "./AIRoom.tsx";
import { useSessionId } from "./hooks/useSessionId.ts";
import { API_BASE } from "./apiBase";
import { track, trackAppOpened } from "./analytics";

const TOP_INSET = 66;
const BOTTOM_INSET = 107;


const socket = socketIO.connect(API_BASE, {
  transports: ["websocket", "polling", "flashsocket"],
});

const fetchString = `${API_BASE}/api/`;

export function StartScreen() {
  useThemeColor();
  const containerHeight = useContainerHeight();
  const sessionId = useSessionId();

  const [roomName, setRoomName] = useState(""); // todo this is not needed can use roomInput
  const [roomState, setRoomState] = useState("start screen");

  const isMobile = useIsMobile();
  const isStartScreen = roomState === "start screen";
  useScrollTrap(isStartScreen);
  const [openJoin, setopenJoin] = useState(false);
  const [openCreateState, setOpenCreateState] = useState("");

  const [AIRoomState, setAIRoomState] = useState("name-entry");
  // Open the classic room straight into the game at its watermark
  const [resumeGame, setResumeGame] = useState(false);
  // Set right before we navigate away on purpose (the Vipps redirect)
  const leavingOnPurpose = useRef(false);

  const [openMenu, setopenMenu] = useState(false);
  const [roomInput, setRoomInput] = useState("");

  const [shouldShow, setShouldShow] = useState(false);
  const [color, setColor] = useState("");
  const [text, setText] = useState("");
  const [add, setAdd] = useState(false);

  function showInfoMessage(
    type,
    message,
    isAdd,
    duration = "medium",
    isDelay = false,
  ) {
    const getDurationMs = (duration) => {
      switch (duration) {
        case "short":
          return 2500;
        case "medium":
          return 5000;
        case "long":
          return 20000;
        default:
          return 5000; // fallback to medium
      }
    };

    const durationMs = getDurationMs(duration);

    setAdd(!!isAdd);
    setColor(type === "warning" ? "var(--yellow)" : "var(--pink)");

    const show = () => {
      setText(message);
      setShouldShow(true);
      setTimeout(() => setShouldShow(false), durationMs);
    };

    if (isDelay) {
      setTimeout(show, 2000); // Delay before showing the message
    } else {
      show();
    }
  }

  const [firstTime, setFirstTime] = useState(() => {
    const { search } = window.location;
    const backFromVipps = new URLSearchParams(search).has("room");
    if (backFromVipps) return "false";
    const saved = localStorage.getItem("firstTime");
    return saved || "true";
  });

  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) return saved;
    const resolved = i18n.resolvedLanguage || i18n.language;
    if (resolved && SUPPORTED_LANGUAGES.includes(resolved)) return resolved;
    return "nb-NO";
  });

  const { t } = useTranslation();

  useEffect(() => {
    trackAppOpened(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLanguage = (code) => {
    if (code !== language) track("language_changed", { from: language, to: code });
    setLanguage(code);
  };

  const finishTutorial = (value) => {
    if (value === "false") track("tutorial_completed");
    setFirstTime(value);
  };

  const [isMenuAnimating, setIsMenuAnimating] = useState(false);

  // Override root background to black when menu is fully open
  // so the animation doesn't show above/below the menu.
  // Delay 500ms on open (wait for slide-in animation).
  // Clear immediately when close animation starts (isMenuAnimating = true).
  useEffect(() => {
    if (openMenu && !isMenuAnimating) {
      const t = setTimeout(() => {
        document.documentElement.style.backgroundColor = "black";
        document.body.style.backgroundColor = "black";
      }, 500);
      return () => clearTimeout(t);
    } else {
      document.documentElement.style.backgroundColor = "";
      document.body.style.backgroundColor = "";
    }
  }, [openMenu, isMenuAnimating]);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isRightSwipe) setopenMenu(true);
    if (isLeftSwipe) {
      setIsMenuAnimating(true);
      setTimeout(() => {
        setIsMenuAnimating(false);
        setopenMenu(false);
      }, 500); // Adjust the timeout based on your animation duration
    }
  };

  useEffect(() => {
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);

    localStorage.setItem("firstTime", firstTime);
  }, [language, firstTime]);

  useEffect(() => {
    const inRoom = roomState === "classic room" || roomState === "ai room";
    if (!inRoom) return;
    const handler = (e) => {
      if (leavingOnPurpose.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [roomState]);

  const onInputChange = (e) => {
    const input = e.currentTarget.value;
    if (/^[^!-\/:-@\[-`{-~]+$/.test(input) || input === "") {
      setRoomInput(input);
    }
  };

  const joinRoom = () => {
    if (roomInput.trim() !== "") {
      enterRoom(roomInput).then((type) => {
        if (type) track("room_joined", { room_type: type }, normalizedInput());
      });
    }
    document.getElementById("page").scrollIntoView();
  };

  const enterRoom = async (name, resume = false) => {
    const normalized = name.toLowerCase().trim();
    const classicRoom = await fetchClassicRoom(normalized);
    if (classicRoom) {
      setRoomName(normalized);
      setResumeGame(resume);
      setRoomState("classic room");
      setopenJoin(false);
      return "classic";
    }
    const aiRoom = await fetchAIRoom(normalized);
    if (aiRoom) {
      setRoomName(normalized);
      const alreadyJoined = (aiRoom.players || []).some(
        (p) => p.sessionId === sessionId && p.name,
      );
      const ownerSetupPending =
        aiRoom.ownerSessionId === sessionId && aiRoom.ownerSetupDone === false;
      const resumeStage =
        aiRoom.ownerSetupStage === "instructions" ? "instructions" : "settings";
      setAIRoomState(
        alreadyJoined ? (ownerSetupPending ? resumeStage : "lobby") : "name-entry",
      );
      setRoomState("ai room");
      setopenJoin(false);
      return "premium";
    }
    showInfoMessage("warning", t("no-exist"), false);
    return false;
  };

  const fetchClassicRoom = async (roomName) => {
    const data = await fetch(fetchString + "classic/" + roomName, {
      headers: { Accept: "application/json" },
    }).then((res) => res.json());
    return JSON.stringify(data) !== "{}";
  };

  const fetchAIRoom = async (roomName) => {
    const data = await fetch(fetchString + "ai/" + roomName, {
      headers: { Accept: "application/json" },
    }).then((res) => res.json());
    return data.data || null;
  };

  const idIsFree = async (input) => {
    const classic = await fetchClassicRoom(input);
    if (classic) return false;
    const ai = await fetchAIRoom(input);
    return !ai;
  };

  const normalizedInput = () => roomInput.toLowerCase().trim();

  // A name starting with "ai " makes a premium (AI) room. `trigger` is how
  // it was submitted: "button", or "enter" in the name field.
  const createRoom = async (trigger = "button") => {
    if (roomInput.trim() !== "") {
      const free = await idIsFree(roomInput.toLowerCase().trim());
      if (free) {
        const type = normalizedInput().startsWith("ai ") ? "premium" : "classic";
        track("room_create_submitted", { room_type: type, trigger });
        // socket.emit(
        //   "create " + openCreateState,
        //   roomInput.toLowerCase().trim(),
        // );
        // setRoomName(roomInput.toLowerCase().trim());
        // setRoomState(openCreateState);
        // setOpenCreateState("");

        if (type === "premium") {
          socket.emit("create ai room", {
            roomName: roomInput.toLowerCase().trim(),
            sessionId,
            trigger,
          });
          setRoomName(roomInput.toLowerCase().trim());
          setRoomState("ai room");
          setAIRoomState("name-entry");
          setOpenCreateState("");
        } else {
          socket.emit(
            "create " + openCreateState,
            roomInput.toLowerCase().trim(),
            { sessionId, trigger },
          );
          setRoomName(roomInput.toLowerCase().trim());
          setResumeGame(false);
          setRoomState(openCreateState);
          setOpenCreateState("");
        }
      } else showInfoMessage("warning", t("taken"), false);
    } else showInfoMessage("warning", t("room-id-missing"), false);
    document.getElementById("page").scrollIntoView();
  };

  // Back from Vipps: the server's /redirect sends the payer here with the
  // room, where they were (lobby or game) and how the payment went.
  const returnParams = useRef(new URLSearchParams(window.location.search));
  useEffect(() => {
    const params = returnParams.current;
    returnParams.current = null;
    const room = params?.get("room");
    if (!room) return;
    window.history.replaceState(null, "", window.location.pathname);
    const payment = params.get("payment");
    enterRoom(room, params.get("view") === "game").then((entered) => {
      if (!entered) return;
      if (payment === "complete") {
        showInfoMessage("", t("payment.status.complete"), false);
      } else if (payment === "aborted") {
        showInfoMessage("warning", t("payment.status.aborted"), false);
      }
    });
  });

  const navigateAway = (url) => {
    leavingOnPurpose.current = true;
    window.location.href = url;
  };

  const openJoinFunc = () => {
    if (!openJoin) track("join_panel_opened");
    setopenJoin(!openJoin);
  };

  const openCreateFunc = (state) => {
    if (state !== "") track("create_panel_opened");
    setOpenCreateState(state);
  };

  const closeMenu = () => {
    setIsMenuAnimating(true);
    setTimeout(() => {
      setIsMenuAnimating(false);
      setopenMenu(false);
    }, 500); // Adjust the timeout based on your animation duration
  };

  function renderAppContent() {
    if (roomState === "classic room") {
      return firstTime === "true" ? (
        <Tutorial setFirstTime={finishTutorial} />
      ) : (
        <NewRoom
          socket={socket}
          roomName={roomName.toLowerCase().trim()}
          setRoomState={setRoomState}
          containerHeight={containerHeight}
          showInfoMessage={showInfoMessage}
          fetchString={fetchString + "classic/"}
          resumeGame={resumeGame}
          navigateAway={navigateAway}
          sessionId={sessionId}
        />
      );
    } else if (roomState === "ai room") {
      return (
        <AIRoom
          roomName={roomName}
          setRoomState={setRoomState}
          AIRoomState={AIRoomState}
          socket={socket}
          setAIRoomState={setAIRoomState}
          containerHeight={containerHeight}
          showInfoMessage={showInfoMessage}
          fetchString={fetchString + "ai/"}
          sessionId={sessionId}
        />
      );
    }

    return (
      <>
        {openMenu && (
          <Menu
            isMenuAnimating={isMenuAnimating}
            containerHeight={containerHeight}
            setLanguage={changeLanguage}
            language={language}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            closeMenu={closeMenu}
            t={t}
          />
        )}

        {openCreateState === "" && !openJoin && (
          <StartPage
            openCreateFunc={openCreateFunc}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            setopenMenu={setopenMenu}
            openJoinFunc={openJoinFunc}
            t={t}
          />
        )}

        {openCreateState !== "" && (
          <CreateRoom
            openCreateFunc={openCreateFunc}
            roomInput={roomInput}
            onInputChange={onInputChange}
            createRoom={createRoom}
            t={t}
          />
        )}

        {openJoin && (
          <JoinRoom
            openJoinFunc={openJoinFunc}
            roomInput={roomInput}
            onInputChange={onInputChange}
            joinRoom={joinRoom}
            t={t}
          />
        )}
      </>
    );
  }

  return (
    <>
      <InfoMessage
        shouldShow={shouldShow}
        color={color}
        text={text}
        add={add}
        setShouldShow={setShouldShow}
      />

      {/* Mobile: overflowX hidden + relative needed for liquid glass scroll trap */}
      {/* Desktop: full overflow hidden to prevent scrolling */}
      <div
        id="page"
        style={
          isMobile
            ? { position: "relative", overflowX: "hidden" }
            : { position: "relative", overflow: "hidden", height: "100vh" }
        }
      >
        {/* Top spacer: only on start screen + mobile for liquid glass hack */}
        {isStartScreen && isMobile && (
          <div style={{ height: TOP_INSET, pointerEvents: "none" }} />
        )}
        <div
          className="App"
          style={
            isStartScreen && isMobile
              ? {
                  height: containerHeight - 44,
                  paddingTop: -44 + TOP_INSET,
                  paddingBottom: 24 + BOTTOM_INSET,
                }
              : { height: containerHeight - 48 }
          }
        >
          {renderAppContent()}
        </div>

        <Background roomState={roomState} containerHeight={containerHeight} />
      </div>
    </>
  );
}
